import { useState } from "react";
import { Github, X, Loader2 } from "lucide-react";
import { api, type GitHubInstallation } from "@/lib/api";
import { StatusDot } from "@/components/ui/status-dot";

interface GitHubAccountsSectionProps {
  installations: GitHubInstallation[];
  onInstallationsChange: (installations: GitHubInstallation[]) => void;
  onProjectRefresh?: () => void;
  onClose: () => void;
}

export function GitHubAccountsSection({
  installations,
  onInstallationsChange,
  onProjectRefresh,
  onClose,
}: GitHubAccountsSectionProps) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  async function handleDisconnect(id: string) {
    setDisconnecting(true);
    try {
      await api.github.disconnect(id);
    } catch (err) {
      console.error("[GitHubAccountsSection] disconnect failed:", err);
    }
    // Always update UI — even if the API call failed, refresh to get true state
    onInstallationsChange(installations.filter((i) => i.id !== id));
    onProjectRefresh?.();
    setDisconnecting(false);
    setConfirmingId(null);
  }

  return (
    <section>
      <h3 className="text-2xs uppercase tracking-widest text-daydra-muted mb-2.5 px-1">
        GitHub
      </h3>
      <div className="rounded-xl p-3.5 bg-white/[0.04] border border-white/[0.06] space-y-2">
        {installations.length > 0 ? (
          installations.map((inst) => (
            <div key={inst.id}>
              {confirmingId === inst.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-daydra-dim flex-1 truncate">
                    Disconnect {inst.account_login}?
                  </span>
                  <button
                    onClick={() => handleDisconnect(inst.id)}
                    disabled={disconnecting}
                    className="text-2xs text-daydra-error hover:text-daydra-error/80 transition-colors"
                  >
                    {disconnecting ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      "Yes"
                    )}
                  </button>
                  <button
                    onClick={() => setConfirmingId(null)}
                    className="text-2xs text-daydra-muted hover:text-daydra-dim transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Github size={13} className="text-daydra-muted shrink-0" />
                  <span className="text-xs text-daydra-dim truncate flex-1">
                    {inst.account_login}
                  </span>
                  <StatusDot
                    status={inst.suspended_at ? "warning" : "connected"}
                    size="sm"
                  />
                  <button
                    onClick={() => setConfirmingId(inst.id)}
                    className="text-daydra-muted hover:text-daydra-dim transition-colors shrink-0"
                    aria-label={`Disconnect ${inst.account_login}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <button
            onClick={() => {
              window.open(api.github.oauthUrl(), "_blank");
              onClose();
            }}
            className="flex items-center gap-2 w-full text-xs text-daydra-muted hover:text-daydra-accent transition-colors duration-fast"
          >
            <Github size={13} />
            <span>Connect GitHub</span>
          </button>
        )}

        {installations.length > 0 && (
          <button
            onClick={() => {
              window.open(api.github.oauthUrl(), "_blank");
              onClose();
            }}
            className="flex items-center gap-2 w-full text-xs text-daydra-muted hover:text-daydra-accent transition-colors duration-fast pt-1"
          >
            <span className="text-xs">+</span>
            <span>Connect another account</span>
          </button>
        )}
      </div>
    </section>
  );
}
